import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
} from "react";
import type { Dayjs } from "dayjs";
import {
  DownloadOutlined,
  LeftOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { message } from "antd";
import { ChevronDown, ChevronUp, Filter } from "react-feather";
import { useNavigate, useParams } from "react-router-dom";

import CustomDatePicker from "../../CommonComponents/CustomDatePicker/CustomDatePicker";
import { Button, PillTabs, type PillTabsItem } from "../../shared";
import AppContext from "../../../context/AppContext";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { SIDER_TABS } from "../../../utils/Constants";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import { useAffiliate } from "../../../queries/affiliates/useAffiliate";
import { useAffiliatePromoCodes } from "../../../queries/affiliates/useAffiliatePromoCodes";
import { useAffiliateDetailReportDownload } from "../../../queries/affiliates/useAffiliateReportDownloads";

import AffiliateProfileKpisPanel from "./AffiliateProfileKpisPanel/AffiliateProfileKpisPanel";
import AffiliatePromoCodes from "./AffiliatePromoCodes/AffiliatePromoCodes";
import AffiliateUsers from "./AffiliateUsers/AffiliateUsers";
import AffiliateInfo from "./AffiliateInfo/AffiliateInfo";
import EditPortalSettingsModal from "./AffiliateInfo/EditPortalSettingsModal";

import styles from "./AffiliateProfile.module.css";
import { getAdminNow } from "../../../utils/dateService";

type AppContextValue = {
  setActiveSidebarTab: (
    tabName: string,
    dropdownOption?: string | false,
  ) => void;
};

type DateRange = [Dayjs, Dayjs];

type CustomDatePickerProps = {
  dates: DateRange;
  setDates: (dates: DateRange) => void;
  getData: () => void;
  showQuickSelect?: boolean;
  showPresets?: boolean;
  startRange?: string;
};
const TypedCustomDatePicker =
  CustomDatePicker as unknown as FC<CustomDatePickerProps>;

type AffiliateProfileTab = "kpis" | "promoCodes" | "users" | "affiliateInfo";

const AFFILIATE_PROFILE_TABS: ReadonlyArray<PillTabsItem<AffiliateProfileTab>> = [
  {
    id: "kpis",
    label: AppStrings.KPIS,
    elementId: "affiliate-profile-tab-kpis",
    ariaControls: "affiliate-profile-panel-kpis",
  },
  {
    id: "promoCodes",
    label: AppStrings.PROMO_CODES,
    elementId: "affiliate-profile-tab-promoCodes",
    ariaControls: "affiliate-profile-panel-promoCodes",
  },
  {
    id: "users",
    label: AppStrings.USERS,
    elementId: "affiliate-profile-tab-users",
    ariaControls: "affiliate-profile-panel-users",
  },
  {
    id: "affiliateInfo",
    label: AppStrings.AFFILIATE_INFO,
    elementId: "affiliate-profile-tab-affiliateInfo",
    ariaControls: "affiliate-profile-panel-affiliateInfo",
  },
];

const AffiliateProfile: FC = () => {
  const navigate = useNavigate();
  const { affiliateId } = useParams<{ affiliateId?: string }>();
  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;

  const [dates, setDates] = useState<DateRange>([
    getAdminNow().startOf("month"),
    getAdminNow(),
  ]);
  // Default to the KPIs overview — first tab in the strip and the
  // highest-level summary of the affiliate's performance, so it's the
  // most useful landing view.
  const [activeTab, setActiveTab] = useState<AffiliateProfileTab>("kpis");
  const [isPortalSettingsOpen, setPortalSettingsOpen] = useState(false);
  // KPIs-tab filter dropdown state — controls the collapsible Code filter
  // rendered by `AffiliateProfileKpisPanel`. Lifted here because the
  // toggle button lives next to the tab strip, at the parent's level.
  const [showKpisFilters, setShowKpisFilters] = useState(false);
  // Code selection is also lifted so it can feed both the panel Select
  // and the analytics hooks downstream. No `selectedAffiliate` — the
  // affiliate is already scoped by the URL.
  const [selectedCode, setSelectedCode] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.PROMOTIONS, "AFFILIATES");
  }, [setActiveSidebarTab]);

  const decodedAffiliateId = useMemo(() => {
    if (!affiliateId) return "";
    try {
      return decodeURIComponent(affiliateId);
    } catch {
      return affiliateId;
    }
  }, [affiliateId]);

  // Options catalog for the Code filter dropdown. Scoped to this affiliate
  // and the header's date range; capped at 100 rows (same as the module
  // KPIs page). Lifted from `AffiliateProfileKpisPanel` so the picked
  // value can also feed the charts inside `AffiliateKpisOverview`.
  const CODE_OPTIONS_LIMIT = 100;
  const codeOptionsQuery = useAffiliatePromoCodes(
    decodedAffiliateId || undefined,
    {
      limit: CODE_OPTIONS_LIMIT,
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    },
  );
  const codeOptions = useMemo(
    () =>
      (codeOptionsQuery.data?.items ?? []).map((code) => ({
        value: code.promo_code,
        label: code.promo_code,
      })),
    [codeOptionsQuery.data?.items],
  );

  const { data: affiliateDetail, error: affiliateError } = useAffiliate(
    decodedAffiliateId || undefined,
  );

  useEffect(() => {
    const status = (affiliateError as { response?: { status?: number } } | null)
      ?.response?.status;
    if (status === 404) {
      message.error(
        getApiErrorMessage(affiliateError, AppStrings.PAM_GENERIC_REQUEST_FAILED),
      );
      navigate(NAVIGATION_ROUTES.MARKETING_AFFILIATES);
    }
  }, [affiliateError, navigate]);

  const pageTitle = useMemo(
    () =>
      affiliateDetail?.contact_name ||
      affiliateDetail?.company_name ||
      decodedAffiliateId,
    [affiliateDetail, decodedAffiliateId],
  );
  usePageTitle(pageTitle);

  const handleBack = useCallback(() => {
    navigate(NAVIGATION_ROUTES.MARKETING_AFFILIATES);
  }, [navigate]);

  // Server-side CSV export — BE builds the two-section report (KPI summary +
  // per-code metrics) scoped to the header date range and returns a signed URL.
  const detailDownload = useAffiliateDetailReportDownload();
  const handleDownload = useCallback(() => {
    if (!decodedAffiliateId) return;
    detailDownload.mutate({
      affiliateId: decodedAffiliateId,
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
    });
  }, [decodedAffiliateId, dates, detailDownload]);

  return (
    <>
      <div className={styles.page} data-testid="affiliate-profile">
        <div className={styles.header}>
          <div className={styles.backRow}>
            <div className={styles.backRowLeft}>
              <button
                type="button"
                className={styles.backButton}
                aria-label={AppStrings.BACK_TO_AFFILIATES_LIST}
                onClick={handleBack}
              >
                <LeftOutlined className={styles.backButtonIcon} />
              </button>
            </div>

            <div className={styles.headerActions}>
              <Button
                intent="neutral"
                iconLeft={<SettingOutlined />}
                onClick={() => setPortalSettingsOpen(true)}
                disabled={!affiliateDetail}
                data-testid="affiliate-profile-edit-portal-view"
              >
                {AppStrings.EDIT_PORTAL_VIEW}
              </Button>
              <div
                className={styles.datePicker}
                data-testid="affiliate-profile-date-picker"
              >
                <TypedCustomDatePicker
                  dates={dates}
                  setDates={setDates}
                  getData={() => {}}
                  showQuickSelect
                  showPresets
                  startRange={AppStrings.THIS_MONTH}
                />
              </div>
              <Button
                intent="neutral"
                iconLeft={<DownloadOutlined />}
                aria-label={AppStrings.DOWNLOAD}
                loading={detailDownload.isPending}
                disabled={!decodedAffiliateId}
                onClick={handleDownload}
              />
            </div>
          </div>

          <div className={styles.tabsRow}>
            <PillTabs<AffiliateProfileTab>
              items={AFFILIATE_PROFILE_TABS}
              value={activeTab}
              onChange={setActiveTab}
              ariaLabel={AppStrings.AFFILIATES}
              data-testid="affiliate-profile-tabs"
            />

            {activeTab === "kpis" && (
              <Button
                variant="solid"
                intent="primary"
                iconLeft={<Filter size={16} />}
                aria-label={AppStrings.AFFILIATE_KPIS_FILTERS}
                aria-expanded={showKpisFilters}
                onClick={() => setShowKpisFilters((prev) => !prev)}
                data-testid="affiliate-profile-kpis-filters-toggle"
              >
                {showKpisFilters ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </Button>
            )}
          </div>
        </div>

        <div
          className={styles.tabPanel}
          role="tabpanel"
          id={`affiliate-profile-panel-${activeTab}`}
          aria-labelledby={`affiliate-profile-tab-${activeTab}`}
          data-testid={`affiliate-profile-panel-${activeTab}`}
        >
          {activeTab === "kpis" && (
            <AffiliateProfileKpisPanel
              affiliateId={decodedAffiliateId}
              dates={dates}
              showFilters={showKpisFilters}
              selectedCode={selectedCode}
              onCodeChange={setSelectedCode}
              codeOptions={codeOptions}
              isLoadingCodes={codeOptionsQuery.isFetching}
            />
          )}
          {activeTab === "promoCodes" && (
            <AffiliatePromoCodes
              affiliateId={decodedAffiliateId}
              startDate={dates[0].format("YYYY-MM-DD")}
              endDate={dates[1].format("YYYY-MM-DD")}
            />
          )}
          {activeTab === "users" && (
            <AffiliateUsers
              affiliateId={decodedAffiliateId}
              startDate={dates[0].format("YYYY-MM-DD")}
              endDate={dates[1].format("YYYY-MM-DD")}
            />
          )}
          {activeTab === "affiliateInfo" && (
            <AffiliateInfo
              affiliateId={decodedAffiliateId}
              detail={affiliateDetail ?? null}
            />
          )}
        </div>

        {affiliateDetail && (
          <EditPortalSettingsModal
            open={isPortalSettingsOpen}
            affiliate={affiliateDetail}
            onClose={() => setPortalSettingsOpen(false)}
          />
        )}
      </div>
    </>
  );
};

export default AffiliateProfile;
